/**
 * البالتة (D72 وD75 وD76): محور مستقل تمامًا عن الوضع (داكن/فاتح).
 *
 * المحور سمة بيانات على `:root`: `data-palette` — و**غيابها يعني `amber`**
 * (D76). فلا تكرار لقيم البالتة الافتراضية في CSS، ولا سمة تُكتب بلا داعٍ.
 * قيم كل بالتة في `styles/palettes.css`، ولا يعرف هذا الملف لونًا واحدًا.
 *
 * **وكل البالتات داكنة** — حُذف الوضع الفاتح وجذورُه كلّها بأمر المالك
 * 2026-08-11. وكان قرار D75 يقول إن الستّ غير `amber` «داكنة فقط» وتسقط إلى
 * قيم amber الفاتحة عند `data-theme='light'`، فكان المبدّل يعرض ملاحظةً
 * تعتذر عن ذلك. سقطت الملاحظة مع سقوط الوضع.
 *
 * والكتابة في `localStorage` تحدث **دائمًا حتى للقيمة الافتراضية** (D76): بلا
 * ذلك لا يستطيع سكربت ما قبل الرسم التمييز بين «اختار amber صراحة» و«لم يختر
 * قطّ» — وهو تمييز حرج للوضع ثلاثي القيم `auto|dark|light`.
 */
export type PaletteId =
  'amber' | 'workspace' | 'midnight' | 'violet' | 'teal' | 'sand' | 'onyx'
  | 'azure'
  | 'forest'
  | 'rose'
  | 'slate'
  | 'crimson'
  | 'lavender'
  | 'olive'
  | 'emerald'
  | 'sunset'
  | 'sky'
  | 'plum'
  | 'moss';

export interface PaletteMeta {
  readonly id: PaletteId;
  /** اسم عربي قصير يظهر تحت الشريحة. */
  readonly label: string;
  /** وصف بجملة واحدة لقارئ الشاشة وللـ title. */
  readonly gist: string;
}

/** الترتيب هو ترتيب العرض في المبدّل: الافتراضي أولًا. */
export const PALETTES: readonly PaletteMeta[] = [
  { id: 'onyx', label: 'فحم', gist: 'شبه أسود محايد بلا صبغة ولكنة فولاذية — الهوية الافتراضية (اتجاه Woblo).' },
  { id: 'amber', label: 'كهرمان', gist: 'حِبر داكن وضوء مصباح كهرماني — وقيمُه هي قيم الوضع الفاتح لكل البالتات.' },
  { id: 'workspace', label: 'زمرّد', gist: 'سليت بارد ولكنة زمرّدية هادئة.' },
  { id: 'midnight', label: 'منتصف الليل', gist: 'أزرق ليلي عميق.' },
  { id: 'violet', label: 'بنفسج', gist: 'بنفسجي داكن.' },
  { id: 'teal', label: 'أزرق مخضر', gist: 'أخضر مزرق صافٍ.' },
  { id: 'sand', label: 'رمل', gist: 'بنّي رملي دافئ.' },
  { id: 'azure', label: 'أزرق دافئ', gist: 'أزرق رمادي — هدوء البحر مساءً' },
  { id: 'forest', label: 'أخضر دافئ', gist: 'أخضر مريم — هدوء الغابة' },
  { id: 'rose', label: 'وردي دافئ', gist: 'وردي مدخّن — هدوء الفجر' },
  { id: 'slate', label: 'رمادي هادئ', gist: 'حجري نقي — لا لون يلهي' },
  { id: 'crimson', label: 'قرمزي', gist: 'نبيذيّ مدخّن — مساء عميق' },
  { id: 'lavender', label: 'خزامى', gist: 'بنفسجيّ هادئ — لمسة فجر' },
  { id: 'olive', label: 'زيتوني', gist: 'خردلي أرضي — ظلّ شجرة قديمة' },
  { id: 'emerald', label: 'زمردي', gist: 'أخضر مشبع — جوهرة عميقة' },
  { id: 'sunset', label: 'غروب', gist: 'مرجاني دافئ — أفق الشمس الغاربة' },
  { id: 'sky', label: 'سماوي', gist: 'سماوي ناعم — صفاء صباح بارد' },
  { id: 'plum', label: 'برقوقي', gist: 'بنفسجيّ وردي — ثمر ناضج' },
  { id: 'moss', label: 'طحلبي', gist: 'أخضر زيتيّ هادئ — ظلّ شجر دائم' },
];

export const DEFAULT_PALETTE: PaletteId = 'onyx';

const STORAGE_KEY = 'flow-palette';

function isPaletteId(value: unknown): value is PaletteId {
  return typeof value === 'string' && PALETTES.some((palette) => palette.id === value);
}

export function readPalette(): PaletteId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isPaletteId(stored) ? stored : DEFAULT_PALETTE;
  } catch {
    return DEFAULT_PALETTE;
  }
}

export function applyPalette(palette: PaletteId): void {
  const root = document.documentElement;
  // «amber» = حذف السمة لا كتابتها (D76): قيم :root الخام هي البالتة الافتراضية.
  if (palette === DEFAULT_PALETTE) root.removeAttribute('data-palette');
  else root.setAttribute('data-palette', palette);
}

export function setPalette(palette: PaletteId): void {
  try {
    localStorage.setItem(STORAGE_KEY, palette);
  } catch {
    // التخزين المحلي محجوب (Safari في وضع خاص): البالتة تبقى لهذه الجلسة فقط.
  }
  applyPalette(palette);
}

/**
 * يُنادى مرة واحدة في الإقلاع. سكربت ما قبل الرسم في `index.html` طبّق البالتة
 * أصلًا (فلا وميض)، وهذا النداء يضمن أن حالة الـ DOM تطابق التخزين بعد أي
 * إعادة تحميل ساخنة في التطوير.
 */
export function initPalette(): void {
  applyPalette(readPalette());
}
